import { Button, Modal, Space, Table } from "antd";
import React, { useCallback } from "react";
import { useIntl } from "react-intl";
import { Link, useParams } from "react-router-dom";
import { StyledButton } from "~Common/Components";
import { PaginationComponent } from "~Common/Pagination/PaginationComponent";
import PermissionCheck from "../../../Common/PermissionCheck";
import { useDeleteSeason } from "~entities/api";
import { PermissionNames, PermissionOperations } from "~enums/permissions";

const useGetColumns = ({handleRemoveSeason, modal}) => {
  const {formatMessage} = useIntl();
  const {organizationId, sport, customerId} = useParams();

  return [
    {
      dataIndex: "images",
      render: (images) =>
        images?.[0] ? (
          <img height={30} width={30} src={images?.[0]?.url} alt="Logo"/>
        ) : (
          <div className={"text-center"}>
            <i className="fas fa-shield fa-xl"></i>
          </div>
        ),
      width: 1,
    },
    {
      title: formatMessage({
        id: "season.title",
        defaultMessage: "Season Title",
      }),
      dataIndex: "nameLocal",
    },
    {
      title: formatMessage({
        id: "gender",
        defaultMessage: "Gender",
      }),
      dataIndex: "gender",
    },
    {
      title: formatMessage({
        id: "standard",
        defaultMessage: "Standard",
      }),
      dataIndex: "standard",
    },
    {
      title: formatMessage({
        id: "ageGroup",
        defaultMessage: "Age Group",
      }),
      dataIndex: "ageGroup",
    },
    {
      title: formatMessage({
        id: "year",
        defaultMessage: "Year",
      }),
      dataIndex: "year",
      sorter: (a, b) => (a.year || 0) - (b.year || 0),
      defaultSortOrder: "descend",
    },
    {
      width: 1,
      render: (record) => {
        return (
          <Space size={0}>
            <PermissionCheck permissionKeys={[PermissionNames.SEASON]}>
              <Link to={`/${customerId}/admin/${sport}/${organizationId}/seasons/${record?.seasonId}/fixtures`}>
                <StyledButton type={"text"}>
                  {formatMessage({
                    id: "manage",
                    defaultMessage: "Manage",
                  })}
                </StyledButton>
              </Link>
            </PermissionCheck>
            <PermissionCheck
              permissionKeys={[PermissionNames.SEASON]}
              permissionOptions={[PermissionOperations.DELETE]}
            >
              <Button
                type={"text"}
                onClick={() => {
                  modal.confirm({
                    title: formatMessage({
                      id: "season.delete",
                      defaultMessage: "Delete Season",
                    }),
                    content: formatMessage(
                      {
                        id: "season.delete.confirm",
                        defaultMessage: "You are about to delete season. Is this OK?",
                      },
                      {item: record?.nameLocal},
                    ),
                    okText: formatMessage({
                      id: "delete",
                      defaultMessage: "Delete",
                    }),
                    onOk: () => handleRemoveSeason(record?.seasonId),
                    cancelButtonProps: {type: "text"},
                    okButtonProps: {type: "danger"},
                  });
                }}
              >
                <i className={"fas fa-trash"}></i>
              </Button>
            </PermissionCheck>
          </Space>
        );
      },
    },
  ];
}


const SeasonTable = ({
  loading,
  seasonList,
  currentPage,
  pageSize,
  handlePaginationChange,
  pageSizeOptions,
}) => {
  const [modal, contextHolder] = Modal.useModal();

  const {mutate: deleteFn, isPending: isDeletePending} = useDeleteSeason();

  const handleRemoveSeason = useCallback(
    (seasonId) => {
      deleteFn(seasonId);
    },
    [deleteFn],
  );

  const columns = useGetColumns(
    {
      handleRemoveSeason,
      modal
    }
  )

  return (
    <>
      {contextHolder}
      <Table
        loading={loading || isDeletePending}
        dataSource={seasonList}
        rowKey={(record) => record?.seasonId}
        columns={columns}
        pagination={{
          current: currentPage,
          defaultPageSize: pageSize,
          showSizeChanger: true,
          pageSizeOptions: pageSizeOptions,
          showQuickJumper: false,
          total: seasonList.length,
          position: ["bottomCenter"],
          onChange: handlePaginationChange,
          showTotal: (total, range) => (
            <PaginationComponent pageNumber={currentPage} pageSize={pageSize} total={total} range={range}/>
          ),
        }}
        size={"small"}
      />
    </>
  );
};

export default SeasonTable;
